import React from 'react';
import { ResponsiveBar } from '@nivo/bar';

const CustomBar = ({
    data,
    height,
    isCustom,
    priorityNumber,
    defaultColors,
    filter,
    projectionDays,
    isComparingAgainstAvgCensus,
    lockedTotalBy,
    transferType,
    calcProPercentsBasedOnFilterAndDays,
    itemMargin,
    originalData = [],
    topMargin = 0,
    renderLabel,
    headerFilterPopover = false,
}) => {
    const bottomAxisMargin = headerFilterPopover ? 8 : 20;
    const barComponent = ({ bar }) => {
        const isFirstItemInPriorityData = priorityNumber === 1;
        const fallbackColor = defaultColors[bar.index % defaultColors.length];
        const fillColor = bar.data.data.color || fallbackColor;
        
        const itemId = bar.data.data._id || bar.data.key || bar.data.data.key;
        const originalItem = originalData?.find((item) => item._id === itemId);
        const isPercentageVisible = originalItem?.isPercentageVisible ?? bar.data.data?.isPercentageVisible ?? true;

        const isZeroWidth = bar.width === 0;
        const displayWidth = isZeroWidth ? 0 : Math.max(bar.width, 80);
        const labelX = bar.x + displayWidth / 2;

        return (
            <g transform={`translate(${bar.x},${bar.y})`} className="nineteen_days_bar">
                <rect
                    style={{ borderRadius: "25% 10%" }}
                    width={displayWidth}
                    height={34}
                    fill={fillColor}
                    strokeWidth="0"
                    stroke={fillColor}
                    focusable="false"
                    ry={20}
                />
                {displayWidth > 12 && (
                    <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        x={labelX}
                        y={17}
                        style={{
                            fontFamily: "manrope",
                            fontSize: "13px",
                            fill: "#fff",
                            pointerEvents: "none",
                            fontWeight: "600",
                        }}
                    >
                        {calcProPercentsBasedOnFilterAndDays(
                            bar?.data?.data?.value ?? bar?.data?.data?.total ?? 0,
                            filter,
                            projectionDays
                        )}
                        {isPercentageVisible && (
                            <tspan
                                fillOpacity="0.85"
                                style={{ fontWeight: "400", fontSize: "11px" }}
                            >
                                (
                                {calcProPercentsBasedOnFilterAndDays(
                                    bar.data.data.percentage,
                                    filter,
                                    projectionDays,
                                    true,
                                    isComparingAgainstAvgCensus ||
                                    lockedTotalBy === "census" ||
                                    (isFirstItemInPriorityData && (!transferType || transferType.length === 0))
                                )}
                                %)
                            </tspan>
                        )}
                    </text>
                )}
            </g>
        );
    };

    return (
        <ResponsiveBar
            data={data}
            keys={["value"]}
            indexBy="key"
            margin={{ 
                top: topMargin, 
                right: 12, 
                bottom: bottomAxisMargin, 
                left: 130 // Make room for labels
            }}
            padding={(() => {
                // Calculate padding based on item margin to match list item spacing
                // Higher margin = more spacing needed = higher padding value
                if (itemMargin) {
                    // Convert margin (8-20px) to padding ratio (0.1-0.5)
                    const paddingRatio = Math.max(0.1, Math.min(0.5, (itemMargin - 8) / 24 * 0.4 + 0.1));
                    return paddingRatio;
                }
                return 0.15;
            })()}
            layout="horizontal"
            innerPadding={0}
            valueScale={{ type: "linear" }}
            axisBottom={{
                tickSize: headerFilterPopover ? 3 : 5,
                tickPadding: headerFilterPopover ? 2 : 5,
                tickRotation: 0,
            }}
            axisLeft={{
                tickSize: 0,
                tickPadding: 130, // Move Nivo's default labels out of the way
                renderTick: (tick) => {
                    const line = tick.value; 
                    const item = data.find(d => d.key === line);
                    const originalLine = item?.originalLine;
                    if (!originalLine) return null;

                    return (
                        <g transform={`translate(${tick.x},${tick.y})`}>
                            <foreignObject x="-130" y="-30" width="130" height="60">
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    height: '60px',
                                    width: '130px',
                                    paddingLeft: '5px'
                                }}>
                                    {renderLabel(originalLine)}
                                </div>
                            </foreignObject>
                        </g>
                    );
                }
            }}
            indexScale={{ type: "band", round: true, padding: (() => {
                // Calculate indexScale padding to match item spacing exactly
                let basePadding;
                if (itemMargin && height) {
                    // Calculate available height for bars (accounting for chart margins)
                    const bottomMargin = headerFilterPopover ? 8 : (isCustom && data?.length < 7 ? 28 : 20);
                    const availableBarHeight = height - topMargin - bottomMargin;
                    const itemCount = data?.length || 1;
                    
                    // The checkbox list total height is: (itemCount * 34) + (itemCount + 1) * itemMargin
                    // In Nivo: bandHeight = availableBarHeight / itemCount
                    // We want: spacing = bandHeight * p = itemMargin
                    // So: p = itemMargin / bandHeight = itemMargin / (availableBarHeight / itemCount)
                    const bandHeight = availableBarHeight / itemCount;
                    const calculatedPadding = itemMargin / bandHeight;
                    basePadding = Math.max(0.1, Math.min(0.7, calculatedPadding));
                } else if (itemMargin) {
                    // Fallback: use simplified conversion
                    basePadding = Math.max(0.3, Math.min(0.6, (itemMargin - 8) / 24 * 0.3 + 0.3));
                } else {
                    basePadding = 0.45;
                }
                // Don't add extra spacing for isCustom - use exact calculation
                return basePadding;
            })() }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            enableGridY={false}
            enableGridX={true}
            borderRadius={4}
            labelTextColor="#fff"
            valueFormat={(v, a, b, c) => {
                return `${v}%1531564561644657`;
            }}
            theme={{
                grid: {
                    line: {
                        strokeWidth: 1,
                        strokeDasharray: "12 12",
                    },
                },
            }}
            animate={false}
            barComponent={barComponent}
        />
    );
};

export default CustomBar;
